import { useState } from "react";
import { roundAmount2Dig } from "utils/amount";
import {
  exclusiveToInclusive,
  getItemNetRate,
  withItemDirectDiscount,
  withItemDiscountPercentage,
  withSyncedRatesFromExclusive,
  withSyncedRatesFromInclusive,
} from "utils/salebillRate";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import { useBill } from "hooks/bill";
import Input from "components/ui/Input";
import { Table, THead, TBody, TR, TH, TD } from "components/ui/Table";

export default function PLOrderTable({ docType, tableData, setTableData, billType }) {
  const [selectAll, setSelectAll] = useState(false);
  const { isLotEnabled } = useCompanyPreferences();
  const { hasApplicableGst, companyHasGst } = useBill();

  const handleLotChange = (changedRow, newLotValue) => {
    const newData = tableData?.map((row) =>
      row.ordno === changedRow.ordno && row.caseno === changedRow.caseno
        ? { ...row, slotqty: newLotValue }
        : row
    );
    setTableData(newData);
  };

  const isPL = billType === "PL";
  const isAgentBill = docType === "AGENTBILL";
  const lotEnabled = isLotEnabled();

  return (
    <Table>
      <THead>
        <TR>
          {!isPL && (
            <TH compact className="w-8">
              <Input
                type="checkbox"
                showLabel={false}
                value={selectAll}
                onChange={(e) => {
                  setSelectAll(e.target.checked);
                  setTableData(
                    tableData?.map((row) => ({
                      ...row,
                      selected: e.target.checked,
                    }))
                  );
                }}
              />
            </TH>
          )}
          {isPL && <TH compact className="min-w-[65px]">Challan</TH>}
          <TH compact className="min-w-[55px]">Ord</TH>
          <TH compact className="min-w-[45px] text-center">CID</TH>
          {lotEnabled && <TH compact className="min-w-[45px] text-center">Lot</TH>}
          <TH compact className="min-w-[120px]">Item Name</TH>
          <TH compact className="min-w-[55px] text-center">Qty</TH>
          {isAgentBill && <TH compact className="min-w-[65px] text-right">Rate</TH>}
          {isAgentBill && hasApplicableGst && (
            <TH compact className="min-w-[75px] text-right" title="Rate including tax">
              Rate Incl
            </TH>
          )}
          <TH compact className="min-w-[38px] text-center">UOM</TH>
          {isAgentBill && <TH compact className="min-w-[65px] text-right">Dis Amt</TH>}
          {isAgentBill && <TH compact className="min-w-[55px] text-right">Dis %</TH>}
          {isAgentBill && <TH compact className="min-w-[75px] text-right">Amount</TH>}
          {companyHasGst && <TH compact className="min-w-[45px] text-center">HSN / SAC</TH>}
          {isAgentBill && hasApplicableGst && <TH compact className="min-w-[45px] text-right">Tax%</TH>}
        </TR>
      </THead>
      <TBody>
        {tableData?.map((row) => (
          <TR key={`ord-${row.ordno}-case-${row.caseno}-item-${row.item.itemid}`}>
            {!isPL && (
              <TD compact>
                <Input
                  type="checkbox"
                  showLabel={false}
                  value={row?.selected}
                  onChange={(e) => {
                    const newData = tableData?.map((r) =>
                      r.ordno === row?.ordno &&
                      r.caseno === row?.caseno &&
                      r.item.itemid === row.item.itemid
                        ? { ...r, selected: e.target.checked }
                        : r
                    );
                    setTableData(newData);
                  }}
                />
              </TD>
            )}
            {isPL && <TD compact>{row.challan_no}</TD>}
            <TD compact>{row.ordno}</TD>
            <TD compact className="text-center">
              <Input
                value={row?.caseno}
                readOnly
                isDisabled
                showLabel={false}
                className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
              />
            </TD>
            {lotEnabled ? (
              <TD compact className="text-center">
                <Input
                  value={row.slotqty}
                  onChange={(e) => handleLotChange(row, e.target.value)}
                  showLabel={false}
                  className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
                />
              </TD>
            ) : (
              <input type="hidden" value={row.slotqty} />
            )}
            <TD compact>{row.item.item_full_name}</TD>
            <TD compact className="text-center">
              <Input
                type="number"
                value={Math.round(row?.sqty)}
                onChange={(e) => {
                  const newData = tableData?.map((r) =>
                    r.item.itemid === row.item.itemid
                      ? { ...r, sqty: e.target.value }
                      : r
                  );
                  setTableData(newData);
                }}
                readOnly={isPL}
                isDisabled={isPL}
                showLabel={false}
                className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
              />
            </TD>
            {isAgentBill && (
              <TD compact>
                <Input
                  type="number"
                  value={row?.srate}
                  onChange={(e) => {
                    const newData = tableData?.map((r) =>
                      r.item.itemid === row.item.itemid
                        ? withSyncedRatesFromExclusive(r, e.target.value)
                        : r
                    );
                    setTableData(newData);
                  }}
                  showLabel={false}
                  className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                />
              </TD>
            )}
            {isAgentBill && hasApplicableGst && (
              <TD compact>
                <Input
                  type="number"
                  step="0.01"
                  value={row?.srate_incl ?? exclusiveToInclusive(row?.srate, row?.tax)}
                  onChange={(e) => {
                    const newData = tableData?.map((r) =>
                      r.item.itemid === row.item.itemid
                        ? withSyncedRatesFromInclusive(r, e.target.value)
                        : r
                    );
                    setTableData(newData);
                  }}
                  showLabel={false}
                  className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                />
              </TD>
            )}
            <TD compact className="text-center">{row.item.itemuom}</TD>
            {isAgentBill && (
              <TD compact>
                <Input
                  type="number"
                  value={row?.dis1}
                  onChange={(e) => {
                    const newDiscount = parseFloat(e.target.value);
                    const rate = parseFloat(row?.srate);
                    if (!isNaN(newDiscount) && !isNaN(rate) && newDiscount <= rate) {
                      const newData = tableData?.map((r) =>
                        r.item.itemid === row.item.itemid
                          ? withItemDirectDiscount(r, newDiscount)
                          : r
                      );
                      setTableData(newData);
                    }
                  }}
                  min={0}
                  showLabel={false}
                  className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                />
              </TD>
            )}
            {isAgentBill && (
              <TD compact>
                <Input
                  type="number"
                  min={0}
                  max={99}
                  step="0.01"
                  value={row?.dis2perc || 0}
                  onChange={(e) => {
                    const newData = tableData?.map((r) =>
                      r.item.itemid === row.item.itemid
                        ? withItemDiscountPercentage(r, e.target.value)
                        : r
                    );
                    setTableData(newData);
                  }}
                  showLabel={false}
                  className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                />
              </TD>
            )}
            {isAgentBill && (
              <TD compact className="text-right">
                {roundAmount2Dig(
                  (parseFloat(row?.sqty) || 0) *
                    getItemNetRate(row)
                )}
              </TD>
            )}
            {companyHasGst && (
              <TD compact className="text-center">{row?.item.itmhsnId}</TD>
            )}
            {isAgentBill && hasApplicableGst && (
              <TD compact className="text-right">
                {row?.tax || 0}%
              </TD>
            )}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
